import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthContext';
import { ClassificationLabelsProvider } from './contexts/ClassificationLabelsContext';
import ProtectedRoute from './auth/ProtectedRoute';
import Login from './auth/Login';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import ClassifyDocument from './pages/ClassifyDocument';
import ClassificationReview from './pages/ClassificationReview';
import ManageSCG from './pages/ManageSCG';
import Tearline from './pages/Tearline';
import Testing from './pages/Testing';
import Settings from './pages/Settings';
import FOIAProcessing from './pages/FOIAProcessing';
import FOIAPolicy from './pages/FOIAPolicy';
import UserManagement from './pages/UserManagement';
import PDFViewerSandbox from './pages/PDFViewerSandbox';
import QueryHistory from './pages/QueryHistory';
import FOIADiscovery from './pages/FOIADiscovery';
import MosaicDetection from './pages/MosaicDetection';
import AuditLogViewer from './pages/AuditLogViewer';
import SearchDocuments from './pages/SearchDocuments';
import DocumentsList from './pages/DocumentsList';
import Analytics from './pages/Analytics';
import AskSCG from './pages/AskSCG';
import DeclassReview from './pages/DeclassReview';
import FeedbackManagement from './pages/FeedbackManagement';
import ProvenanceViewer from './pages/ProvenanceViewer';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Layout with page transition animation
function AppLayout() {
  const location = useLocation();
  return (
    <>
      <Navbar />
      <main className="container mx-auto px-4 py-8 page-enter" key={location.pathname}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/classify" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <ClassifyDocument />
            </ProtectedRoute>
          } />
          <Route path="/review" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <ClassificationReview />
            </ProtectedRoute>
          } />
          <Route path="/scg" element={
            <ProtectedRoute requiredPermission="manage_scg">
              <ManageSCG />
            </ProtectedRoute>
          } />
          <Route path="/tearline" element={
            <ProtectedRoute requiredPermission="tearline_sanitize">
              <Tearline />
            </ProtectedRoute>
          } />
          <Route path="/foia" element={
            <ProtectedRoute requiredPermission="foia_process">
              <FOIAProcessing />
            </ProtectedRoute>
          } />
          <Route path="/foia-discovery" element={
            <ProtectedRoute requiredPermission="foia_process">
              <FOIADiscovery />
            </ProtectedRoute>
          } />
          <Route path="/foia-policy" element={
            <ProtectedRoute requiredPermission="foia_process">
              <FOIAPolicy />
            </ProtectedRoute>
          } />
          <Route path="/testing" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <Testing />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <Analytics />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredPermission="manage_configs">
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute requiredPermission="manage_users">
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/pdf-sandbox" element={<PDFViewerSandbox />} />
          <Route path="/history" element={<QueryHistory />} />
          <Route path="/mosaic" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <MosaicDetection />
            </ProtectedRoute>
          } />
          <Route path="/audit" element={
            <ProtectedRoute requiredPermission="manage_users">
              <AuditLogViewer />
            </ProtectedRoute>
          } />
          <Route path="/feedback" element={
            <ProtectedRoute requiredPermission="manage_configs">
              <FeedbackManagement />
            </ProtectedRoute>
          } />
          <Route path="/search" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <SearchDocuments />
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <DocumentsList />
            </ProtectedRoute>
          } />
          <Route path="/ask-scg" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <AskSCG />
            </ProtectedRoute>
          } />
          <Route path="/declass-review" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <DeclassReview />
            </ProtectedRoute>
          } />
          <Route path="/provenance" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <ProvenanceViewer />
            </ProtectedRoute>
          } />
          <Route path="/provenance/:decisionId" element={
            <ProtectedRoute requiredPermission="classify_documents">
              <ProvenanceViewer />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClassificationLabelsProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/*" element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                } />
              </Routes>
            </div>
          </Router>
        </ClassificationLabelsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
